import { useCallback, useEffect, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { useGoogleLogin } from '@react-oauth/google';
import type { SocialLoginMicrosoftToken } from 'api';
import {
  AUTH_MSAL_CONFIG,
  AUTH_SOCIAL_ERROR,
  AUTH_STORAGE_PERSISTENCE_LOCAL,
  type AuthTokenPersistence,
  GOOGLE,
  MICROSOFT,
} from 'common/constants/auth';
import { useGetSocialAccessToken } from 'pages/Auth/hooks/useAuthActions';
import { clearEnterpriseSsoSignInSession } from 'pages/Auth/utils';

type GetSocialAccessToken = ReturnType<typeof useGetSocialAccessToken>;
type SocialAccessTokenPayload = Parameters<GetSocialAccessToken>[0];

let microsoftAuthClient: PublicClientApplication | null = null;
let microsoftAuthClientPromise: Promise<PublicClientApplication> | null = null;

const getMicrosoftAuthClient = async () => {
  if (microsoftAuthClient) {
    return microsoftAuthClient;
  }

  if (!microsoftAuthClientPromise) {
    microsoftAuthClientPromise = (async () => {
      const client = new PublicClientApplication(AUTH_MSAL_CONFIG);
      await client.initialize();
      microsoftAuthClient = client;
      return client;
    })();
  }

  return microsoftAuthClientPromise;
};

interface UseAuthSocialSignInParams {
  getSocialAccessToken: GetSocialAccessToken;
  onSuccess?: () => void;
  persistence?: AuthTokenPersistence;
  setAuthError: (message: string | null) => void;
}

const useAuthSocialSignIn = ({
  getSocialAccessToken,
  onSuccess,
  persistence = AUTH_STORAGE_PERSISTENCE_LOCAL,
  setAuthError,
}: UseAuthSocialSignInParams) => {
  const [socialProviderPending, setSocialProviderPending] = useState<string | null>(null);

  useEffect(() => {
    getMicrosoftAuthClient().catch(() => undefined);
  }, []);

  const handleSocialLogin = useCallback(
    async (payload: SocialAccessTokenPayload) => {
      try {
        const response = await getSocialAccessToken(payload, persistence);

        if (response) {
          setAuthError(AUTH_SOCIAL_ERROR);
          return;
        }

        clearEnterpriseSsoSignInSession();
        onSuccess?.();
      } catch {
        setAuthError(AUTH_SOCIAL_ERROR);
      } finally {
        setSocialProviderPending(null);
      }
    },
    [getSocialAccessToken, onSuccess, persistence, setAuthError]
  );

  const startGoogleLogin = useGoogleLogin({
    flow: 'implicit',
    onError: () => {
      setAuthError(AUTH_SOCIAL_ERROR);
      setSocialProviderPending(null);
    },
    onNonOAuthError: () => {
      setAuthError(AUTH_SOCIAL_ERROR);
      setSocialProviderPending(null);
    },
    onSuccess: ({ access_token }) =>
      handleSocialLogin({
        credential: `Bearer ${access_token}`,
        provider: GOOGLE,
      }),
  });

  const handleGoogleLogin = useCallback(() => {
    if (socialProviderPending) {
      return;
    }

    setAuthError(null);
    setSocialProviderPending(GOOGLE);
    startGoogleLogin();
  }, [setAuthError, socialProviderPending, startGoogleLogin]);

  const handleMicrosoftLogin = useCallback(async () => {
    if (socialProviderPending) {
      return;
    }

    setAuthError(null);
    setSocialProviderPending(MICROSOFT);

    try {
      const microsoftAuthClientInstance = await getMicrosoftAuthClient();
      const response = await microsoftAuthClientInstance.loginPopup();

      await handleSocialLogin({
        id_token: {
          claims: (response.idTokenClaims ?? {}) as SocialLoginMicrosoftToken['claims'],
          rawIdToken: response.idToken,
        },
        provider: MICROSOFT,
      });
    } catch {
      setAuthError(AUTH_SOCIAL_ERROR);
      setSocialProviderPending(null);
    }
  }, [handleSocialLogin, setAuthError, socialProviderPending]);

  return {
    handleGoogleLogin,
    handleMicrosoftLogin,
    isSocialPending: socialProviderPending !== null,
    socialProviderPending,
  };
};

export default useAuthSocialSignIn;
